import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { MarketingHeader, Modal, useAuth, usePrerenderGate } from '@byscott-io/corebyscott';
import { publicRecipes } from '../api/client';
import RecipeBrowser from '../components/recipes/RecipeBrowser';
import useRecipeSeo from '../hooks/useRecipeSeo';

export default function PublicRecipesPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // Per-recipe title/description/ld+json. A crawler now gets this real page,
  // so what it reads is whatever this sets -- there is no Ruby copy of these
  // fields to keep in step.
  useRecipeSeo(selectedRecipe);

  // With an id in the URL this page IS the recipe page, so it must not be
  // snapshotted until the recipe is in hand -- the head tags above are set from
  // it. Deliberately stays closed on a failed load: an unsignalled render is
  // logged and retried, where storing the error state would serve it to
  // crawlers as the recipe's content until the record next changed.
  usePrerenderGate(!id || Boolean(selectedRecipe), 'recipe-detail');

  // Load recipe details when URL has an ID.
  // Authed users get redirected to the in-app page, which has access to
  // their private recipes via the authed API.
  React.useEffect(() => {
    if (id && isAuthenticated) {
      navigate(`/app/recipes/${id}`, { replace: true });
      return;
    }
    if (id) {
      loadRecipeDetails(id);
    } else {
      setSelectedRecipe(null);
      setLoadFailed(false);
    }
  }, [id, isAuthenticated]);

  const loadRecipeDetails = async (recipeId) => {
    try {
      setLoadingRecipe(true);
      setLoadFailed(false);
      const response = await publicRecipes.get(recipeId);
      setSelectedRecipe(response.data.recipe);
    } catch (err) {
      console.error('Failed to load recipe details:', err);
      // Don't bounce to / on failure — leave the modal so the user can close it
      // (or refresh) instead of being silently redirected. The public API
      // cannot distinguish "deleted" from "belongs to a kitchen and is not
      // public", so the message below covers both rather than guessing.
      setLoadFailed(true);
    } finally {
      setLoadingRecipe(false);
    }
  };

  const handleCloseModal = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white dark:bg-gray-800 shadow-sm">
        <MarketingHeader />
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Browse public recipes</h1>
          <Link
            to="/contribute"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
          >
            + Contribute a recipe
          </Link>
        </div>
        <RecipeBrowser
          showActions={false}
          defaultMyKitchen={false}
          detailPath="/recipes"
          stickyTop="top-16"
        />
      </main>

      {/* Recipe Detail Modal */}
      <Modal
        open={!!(id || selectedRecipe)}
        onClose={handleCloseModal}
        title={selectedRecipe?.title}
        size="xl"
        maxHeight="max-h-[90vh]"
      >
        {loadingRecipe ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : selectedRecipe ? (
          <>

                {selectedRecipe.description && (
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{selectedRecipe.description}</p>
                )}

                <div className="flex gap-4 text-sm text-gray-500 dark:text-gray-400 mb-6">
                  {selectedRecipe.prep_time_minutes > 0 && <span>Prep: {selectedRecipe.prep_time_minutes} min</span>}
                  {selectedRecipe.cook_time_minutes > 0 && <span>Cook: {selectedRecipe.cook_time_minutes} min</span>}
                  {selectedRecipe.servings && <span>Servings: {selectedRecipe.servings}</span>}
                </div>

                {selectedRecipe.ingredients?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Ingredients</h3>
                    <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                      {selectedRecipe.ingredients.map((ing) => (
                        <li key={ing.id}>{ing.display_text}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedRecipe.instructions && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Instructions</h3>
                    <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {selectedRecipe.instructions}
                    </div>
                  </div>
                )}

                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-400">Share: Copy the URL from your browser</p>
                </div>
          </>
        ) : loadFailed ? (
          <div className="py-10 text-center">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
              Recipe not available
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This recipe may have been removed, or it belongs to a kitchen and
              has not been shared publicly.
            </p>
            <Link
              to="/recipes"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
              onClick={handleCloseModal}
            >
              Browse public recipes
            </Link>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
